import { Formik, FieldArray } from "formik";
import styles from "./FilterModal.module.css";
import Modal from "../../../Modals/Modal";
import { useLanguage } from "../../../../context/LanguageContext";
import SearchableDropdown from "../../../SearchableDropdown/SearchableDropdown";
import { postUser } from "../../../../helper/http";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTimes } from "@fortawesome/free-solid-svg-icons";
import FilterValueInput from "./FilterValueInput";

interface FilterCondition {
  field: string;
  operator: string;
  value: any;
}

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onApply: (filters: FilterCondition[]) => void;
  fields: any[];
  activeFilters?: FilterCondition[];
  options: Record<any, any[]>;
  setOptions: React.Dispatch<React.SetStateAction<Record<any, any[]>>>;
}

const OPERATORS = [
  { value: "=", label: "Равно" },
  { value: "!=", label: "Не равно" },
  { value: ">", label: "Больше" },
  { value: "<", label: "Меньше" },
  { value: ">=", label: "Больше или равно" },
  { value: "<=", label: "Меньше или равно" },
  { value: "LIKE", label: "Содержит" },
  { value: "NOT LIKE", label: "Не содержит" },
  { value: "IN", label: "Одно из (через запятую)" },
];

export default function FilterModal({
  isOpen,
  onClose,
  onApply,
  fields,
  activeFilters = [],
  options,
  setOptions,
}: FilterModalProps) {

  const { t } = useLanguage();

  const initialValues = {
    conditions: activeFilters.length
      ? activeFilters
      : [
          {
            field: fields[0].name,
            operator: "=",
            value: "",
          },
        ],
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("filterData")}
    >
      <Formik
        initialValues={initialValues}
        enableReinitialize
        onSubmit={(values) => {
          onApply(values.conditions);
          onClose();
        }}
      >
        {({ values, submitForm, setFieldValue }) => (
          <>
            <FieldArray name="conditions">
              {({ push, remove }) => (
                <div className={styles.conditionsList}>

                  {values.conditions.map((cond, index) => {
                    const fieldDef = fields.find(f => f.name === cond.field);

                    return (
                      <div className={styles.filterRow} key={index}>
                        
                        <button
                          type="button"
                          className={styles.deleteBtnTopRight}
                          onClick={() => remove(index)}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>

                        <div className={styles.filterField}>
                          <label className={styles.fieldLabel}>{t("field")}</label>

                          <SearchableDropdown
                            label=""
                            options={fields.map(f => ({
                              label: f.name,
                              value: f.name,
                            }))}
                            value={cond.field}
                            onChange={(v) =>
                              setFieldValue(`conditions.${index}.field`, v)
                            }
                            onSearch={() => {}}
                          />
                        </div>

                        <div className={styles.filterField}>
                          <label className={styles.fieldLabel}>{t("operator")}</label>

                          <SearchableDropdown
                            label=""
                            options={OPERATORS.map(op => ({
                              label: op.label,
                              value: op.value,
                            }))}
                            value={cond.operator}
                            onChange={(v) =>
                              setFieldValue(`conditions.${index}.operator`, v)
                            }
                            onSearch={() => {}}
                          />
                        </div>

                        <div className={styles.filterField}>
                          <label className={styles.fieldLabel}>{t("value")}</label>

                          {fieldDef?.file_type === "fk" ? (
                            <SearchableDropdown
                              label=""
                              value={cond.value}
                              options={[
                                ...(options[fieldDef.relation_module] ?? []),
                                { label: "No value", value: null },
                              ]}
                              onChange={(v) =>
                                setFieldValue(`conditions.${index}.value`, v)
                              }
                              onSearch={async text => {
                                if (!text) return;

                                const payload = {
                                  module_id: +fieldDef.relation_module,
                                  filter: text,
                                };

                                const res = await postUser("crud/select", payload);

                                setOptions(prev => ({
                                  ...prev,
                                  [fieldDef.relation_module]: Object.keys(res.list).map(key => ({
                                    label: res.list[key],
                                    value: key,
                                  })),
                                }));
                              }}
                            />
                          ) : (
                            <FilterValueInput
                              fieldDef={fieldDef}
                              value={cond.value}
                              onChange={(v) =>
                                setFieldValue(`conditions.${index}.value`, v)
                              }
                            />
                          )}
                        </div>
                      </div>
                    );
                  })}

                  <button
                    type="button"
                    className={styles.addBtn}
                    onClick={() =>
                      push({
                        field: fields[0].name,
                        operator: "=",
                        value: "",
                      })
                    }
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    <span className={styles.addBtnLabel}>{t("addCondition")}</span>
                  </button>
                </div>
              )}
            </FieldArray>

            <div className={styles.actions}>

              <button
                type="button"
                className={styles.clearBtn}
                onClick={() => onApply([])}
              >
                {t("clear")}
              </button>

              <button
                type="button"
                className={styles.cancelBtn}
                onClick={onClose}
              >
                {t("cancel")}
              </button>

              <button
                type="button"
                className={styles.applyBtn}
                onClick={submitForm}
              >
                {t("applyFilter")}
              </button>

            </div>
          </>
        )}
      </Formik>
    </Modal>
  );
}
